import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import {
  useFavoriteBusinessMutation,
  useGetBusinessesByUserIdQuery,
} from "lib/features/business/businessService";
import { Business } from "lib/features/business/types";
import { User } from "lib/features/user/types";
import { setUser } from "lib/features/user/userSlice";
import { useAppDispatch } from "lib/hooks";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import authServices from "services/auth-service";
import chatServices from "services/chat-service";
import { businessCardStyles } from "styles/Home/businessCard.styles";
import { formatAddress } from "util/formatAddress";
import { parseImageUrl } from "util/parseImageUrl";
import UnFilledStar from "../../assets/icons/bordered_star.svg";
import EmailFirstIcon from "../../assets/icons/email_one.png";
import FilledStar from "../../assets/icons/filled_star.svg";
import message from "../../assets/icons/message_icon.svg";
import Share from "../../assets/icons/send.png";
import Connect from "../../assets/icons/share.png";
import CustomerModal from "./CustomerModal";
import ShareBusinessModal from "./Home/ShareBusinessModal";


interface CreateConversationDto {
  participants: string[];
  initialMessage: {
    content: string;
    authorId: string;
  };
}

interface BusinessProps {
  business: Business;
  onToggleFavorite: (businessId: number) => void;
}

const BusinessCard: React.FC<BusinessProps> = ({
  business,
  onToggleFavorite,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [loading, setLoading] = useState<boolean>(false);
  const [isFavorite, setIsFavorite] = useState<boolean>(business.isFavorite);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const [favoriteBusiness, { isLoading: isFavoritedLoading }] =
    useFavoriteBusinessMutation();

  const handleOpenModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsModalOpen(true);
  };
  const handleCloseModal = () => setIsModalOpen(false);

  const userData = localStorage.getItem("user") as string;
  const parsedUserObject = JSON.parse(userData) as User | undefined;
  const userId = parsedUserObject?.id;
  const role_id = parsedUserObject?.role_id;

  const { data: userBusinessDetails, isLoading: isLoadingUserBusiness } =
    useGetBusinessesByUserIdQuery({ id: Number(userId) }, {});

  const link = `https://app.refereree.com/business/${business?.id}?referredBy=${userId}`;

  
  const onPressCreateChannel = async (event: React.MouseEvent) => {
    
    event.stopPropagation();
    let channelUrl = "";

    if (userId && business?.business_users[0]?.id) {
      console.log("Creating channel between users:", userId, business?.business_users[0]?.id);
      const payload: CreateConversationDto = {  
        participants: [userId.toString(), business?.business_users[0]?.id.toString()],
        initialMessage: {
          content: "Connecting via Refereree",
          authorId: userId.toString(),
        },
      };
      
      setLoading(true);
      const response = await chatServices.createChannel(payload);
      console.log("Create channel response:", response);
      channelUrl = response?.data?.conversation;
      console.log("Navigating to channelUrl:", channelUrl);
      if (channelUrl) {
        setLoading(false);
        navigate(`/chat/${channelUrl}`);
        return;
      } else {
      }
    }
  };

  const assumeUser = async (event: React.MouseEvent) => {
    event.stopPropagation();
    console.log("Assume user called");

    if (userId && business?.business_users[0]?.id) {
      setLoading(true);
      try {
        const result = await authServices.assume(
          business?.business_users[0]?.id
        );
        const token = result?.data?.token;
        const user: User = result?.data?.user;

        localStorage.clear();
        sessionStorage.clear();

        await new Promise((resolve) => setTimeout(resolve, 50));

        localStorage.setItem("token", token);
        localStorage.setItem("user", JSON.stringify(user));

        dispatch(setUser(user));

        console.log("Assumed user token:", token);

        await new Promise((resolve) => setTimeout(resolve, 100));

        window.location.href = "/my-items";
      } catch (error) {
        setLoading(false);
        console.error("Error assuming user:", error);
        alert("Failed to assume user. Please try again.");
      }
    }
  };

  const deleteUser = async (event: React.MouseEvent) => {
    event.stopPropagation();
    console.log("Delete user called");
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    if (business?.business_users[0]?.id) {
      setLoading(true);
      await authServices
        .delete(business?.business_users[0]?.id)
        .then((response) => {
          console.log("User deleted successfully", response);
          setLoading(false);
          window.location.reload();
        })
        .catch((error) => {
          setLoading(false);
          console.log("Error deleting user: ", error);
        });
    }
  };

  const onPressProfileNavigate = () => {
    navigate(`/business/${business?.id}?showDetails=all`);
  };

  useEffect(() => {
    setIsFavorite(business.isFavorite);
  }, [business.isFavorite]);

  const toggleFavorite = async (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isFavoritedLoading) return;
    setIsFavorite(true);

    if (userId && business.id) {
      try {
        await favoriteBusiness({
          userId: userId,
          businessId: business.id,
        });
        onToggleFavorite(business.id);
      } catch (error) {
        setIsFavorite(false);
      }
    }
  };

  const isOwnBusiness = business?.business_users[0]?.id === userId;

  return (
    <>
      <Card sx={businessCardStyles.card(theme)} onClick={onPressProfileNavigate}>
        <Box sx={{ position: "relative" }}>
          <CardMedia
            alt="Business Image"
            component={"img"}
            height="200px"
            src={
              business?.businessImages[0]?.uri
                ? parseImageUrl(business?.businessImages[0]?.uri)
                : business?.business_users[0]?.avatar
                ? parseImageUrl(business?.business_users[0]?.avatar)
                : "default-image-url"
            }
          />
          <IconButton
            sx={{ position: "absolute", top: 10, right: 10 }}
            onClick={toggleFavorite}
          >
            {isFavorite ? (
              <img src={FilledStar} alt="FavoriteIcon" />
            ) : (
              <img src={UnFilledStar} alt="FavoriteIcon" />
            )}
          </IconButton>
        </Box>

        <CardContent sx={businessCardStyles.cardContent(theme)}>
          <Box minHeight="100px">
            <Typography sx={businessCardStyles.businessNameTypography(theme)}>
              {business.business}
            </Typography>
            <Typography sx={{ fontSize: "11px", color: "#595959" }}>
              {formatAddress(business?.address1, business?.city, business?.state)}
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 500,
                lineHeight: "22.5px",
                letterSpacing: "1.8%",
                color: "#000",
              }}
            >
              {business.business_headline}
            </Typography>
          </Box>

          {isOwnBusiness ? (
            <Box>
              <Button
                sx={businessCardStyles.filledButton}
                disabled={loading}
                onClick={onPressProfileNavigate}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography sx={businessCardStyles.filledButtonTypography}>
                    View Profile
                  </Typography>
                )}
              </Button>
            </Box>
          ) : (
            <Box sx={{ mt: "10px", flexDirection: "row", display: "flex", gap: "16px" }}>
              <Button sx={businessCardStyles.unFilledButton} disabled={loading} onClick={onPressCreateChannel}>
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <Typography
                    sx={{
                      ...businessCardStyles.unFilledButtonTypography,
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                    }}
                  >
                    <img src={EmailFirstIcon} alt="email" width={20} height={20} />
                    <span style={{ fontSize: "9px", paddingTop: "2px" }}>Message</span>
                  </Typography>
                )}
              </Button>

              <Button sx={businessCardStyles.unFilledButton} onClick={handleOpenModal}>
                <Typography
                  sx={{
                    ...businessCardStyles.unFilledButtonTypography,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <img src={Connect} alt="connect" width={12} height={12} />
                  <span style={{ fontSize: "11px", paddingTop: "2px" }}>Connect</span>
                </Typography>
              </Button>

              <Button
                sx={businessCardStyles.unFilledButton}
                onClick={(event: React.MouseEvent) => {
                  event.stopPropagation();
                  setIsShareModalOpen(true);
                }}
              >
                <Typography
                  sx={{
                    ...businessCardStyles.unFilledButtonTypography,
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <img src={Share} alt="share" width={12} height={12} />
                  <span style={{ fontSize: "11px", paddingTop: "2px" }}>Share</span>
                </Typography>
              </Button>
            </Box>
          )}

          {role_id === 5 && (
            <Box sx={{ mt: "10px" }}>
              <Button
                sx={businessCardStyles.deleteButton}
                disabled={loading}
                onClick={deleteUser}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <img
                      src={message}
                      alt="delete"
                      style={businessCardStyles.messageImage}
                    />
                    <Typography sx={businessCardStyles.filledButtonTypography}>
                      Delete User
                    </Typography>
                  </>
                )}
              </Button>
            </Box>
            
          )}
          {role_id === 5 && (
            <Box sx={{ mt: "10px" }}>
              <Button
                sx={businessCardStyles.deleteButton}
                disabled={loading}
                onClick={assumeUser}
              >
                {loading ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <img
                      src={message}
                      alt="delete"
                      style={businessCardStyles.messageImage}
                    />
                    <Typography sx={businessCardStyles.filledButtonTypography}>
                      Assume User
                    </Typography>
                  </>
                )}
              </Button>
            </Box>
            
          )}

         
        </CardContent>
      </Card>

      <ShareBusinessModal
        open={isShareModalOpen}
        close={() => setIsShareModalOpen(false)}
        link={link}
        businessName={business.business}
      />

      <CustomerModal
        open={isModalOpen}
        close={handleCloseModal}
        state={3}
        referral_type={10}
        user_id={Number(business?.business_users[0]?.id)}
        business_id={Number(business?.id)}
        businessName={business.business}
      />
    </>
  );
};

export default BusinessCard;
